import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { useParams } from 'react-router-dom'
import { debounce } from 'lodash'
import {
  Alert,
  Col,
  Input,
  List,
  notification,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
} from 'antd'
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  InfoCircleOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { Button, ButtonActionBar, Navigator, Typography } from 'modules/core/components'
import { SiderLayout } from 'modules/core/layouts'
import { usePagination } from 'modules/core/customHooks'
import { DIMENSIONS, ROLES } from 'modules/core/constants'
import { isUserAllowed } from 'modules/core/utils'
import configuration from 'modules/configuration'
import { notifyMappingError } from '../utils'
import { ImportRowsModal, MapRowModal } from './components'

const { SETTINGS__DIMENSION_MAPPINGS__ABM } = ROLES
const { EMPTY_VALUE_ID } = DIMENSIONS

const SOURCES_SEARCH_DEBOUNCE = 400

const ITEM_CONDITION_LABELS = {
  ARTICULO: 'CONFIG_DIMENSION_MAPPING_ITEM_CONDITION_ARTICLE',
  SERVICIO: 'CONFIG_DIMENSION_MAPPING_ITEM_CONDITION_SERVICE',
}

const DimensionMappingDetail = ({
  dimensionMappingsList,
  dimensionMappingRowsList,
  fetchDimensionMappings,
  fetchDimensionMappingRows,
  fetchDimensionMappingRowsLookup,
  bulkCreateDimensionMappingRows,
  deleteDimensionMappingRow,
  downloadDimensionMappingRows,
  fetchAllDimensions,
  fetchAllDimensionsValues,
  fetchDocumentTypes,
}) => {
  const { sourceDimension } = useParams()
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [onlyUnmapped, setOnlyUnmapped] = useState(false)
  const [targetFilters, setTargetFilters] = useState({})
  const [tablePagination, setTablePagination] = usePagination()
  const [showMapModal, setShowMapModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [deleteId, setDeleteId] = useState(null)
  const [rowsLoaded, setRowsLoaded] = useState(false)
  const [valuesByTarget, setValuesByTarget] = useState({})
  const [unmappedSources, setUnmappedSources] = useState([])
  const [documentTypes, setDocumentTypes] = useState([])
  const [editingRow, setEditingRow] = useState(null)
  const [sourceTotal, setSourceTotal] = useState(null)
  const [unmappedCount, setUnmappedCount] = useState(null)
  const [selectedSources, setSelectedSources] = useState([])
  const [bulkValues, setBulkValues] = useState({})
  const [bulkErrors, setBulkErrors] = useState([])
  const [selectedLabels, setSelectedLabels] = useState({})
  const [mappingSource, setMappingSource] = useState(null)
  const [rowDrafts, setRowDrafts] = useState({})
  const { t } = useTranslation()

  const userCanABM = isUserAllowed(SETTINGS__DIMENSION_MAPPINGS__ABM)
  const mapping = dimensionMappingsList.find((item) => item.source_dimension === sourceDimension)
  const canEdit = userCanABM && mapping?.is_editable
  const hasSingleTarget = mapping?.targets.length === 1
  const isSubtype = mapping?.is_subtype

  const filters = useMemo(
    () => ({
      ...(search && { search }),
      ...(onlyUnmapped && { unmapped: true }),
      ...targetFilters,
    }),
    [search, onlyUnmapped, targetFilters],
  )

  const params = useMemo(() => ({ ...tablePagination, ...filters }), [tablePagination, filters])

  const refetchRows = useCallback(
    (quiet = false) => {
      if (!quiet) setIsLoading(true)
      return fetchDimensionMappingRows(sourceDimension, params)
        .catch(notifyMappingError)
        .finally(() => {
          setRowsLoaded(true)
          setIsLoading(false)
        })
    },
    [fetchDimensionMappingRows, sourceDimension, params],
  )

  const refetchUnmappedCount = useCallback(
    () =>
      fetchDimensionMappingRowsLookup(sourceDimension, { page_size: 1, unmapped: true })
        .then((response) => setUnmappedCount(response.payload.data.count))
        .catch(() => setUnmappedCount(null)),
    [fetchDimensionMappingRowsLookup, sourceDimension],
  )

  const refetchAll = useCallback(
    (quiet = false) => Promise.all([refetchRows(quiet), refetchUnmappedCount()]),
    [refetchRows, refetchUnmappedCount],
  )

  useEffect(() => {
    fetchDimensionMappings()
  }, [fetchDimensionMappings])

  useEffect(() => {
    refetchRows()
  }, [refetchRows])

  useEffect(() => {
    refetchUnmappedCount()
  }, [refetchUnmappedCount])

  useEffect(() => {
    fetchDimensionMappingRowsLookup(sourceDimension, { page_size: 1 })
      .then((response) => setSourceTotal(response.payload.data.count))
      .catch(() => setSourceTotal(null))
  }, [fetchDimensionMappingRowsLookup, sourceDimension])

  useEffect(() => {
    if (!mapping) return

    fetchAllDimensions().then((response) => {
      const dimensions = response.payload.data
      mapping.targets.forEach((target) => {
        const id = dimensions.find((dimension) => dimension.name === target)?.id
        if (id) {
          fetchAllDimensionsValues({ id_dimension: id }).then((values) =>
            setValuesByTarget((prev) => ({
              ...prev,
              [target]: values.payload.data
                .map((value) => ({ ...value, id: parseInt(value.id) }))
                .filter((value) => value.id !== EMPTY_VALUE_ID),
            })),
          )
        }
      })
    })
  }, [mapping, fetchAllDimensions, fetchAllDimensionsValues])

  useEffect(() => {
    if (!isSubtype) return
    fetchDocumentTypes({ manual: true })
      .then((response) => setDocumentTypes(response.payload.data))
      .catch(notifyMappingError)
  }, [isSubtype, fetchDocumentTypes])

  // El listado de orígenes está paginado, así que el filtro por tipo tiene que ir al server.
  const loadUnmappedSources = useCallback(
    (value = '', documentType) =>
      fetchDimensionMappingRowsLookup(sourceDimension, {
        unmapped: true,
        page_size: 50,
        ...(value && { search: value }),
        ...(documentType && { document_type: documentType }),
      })
        .then((response) =>
          setUnmappedSources(
            response.payload.data.results.map((row) => ({
              id: row.source.id,
              code_destination: row.source.code,
              name_destination: row.source.name,
              document_type: row.source.document_type,
            })),
          ),
        )
        .catch(notifyMappingError),
    [fetchDimensionMappingRowsLookup, sourceDimension],
  )

  const searchUnmappedSources = useMemo(
    () => debounce(loadUnmappedSources, SOURCES_SEARCH_DEBOUNCE),
    [loadUnmappedSources],
  )

  useEffect(() => () => searchUnmappedSources.cancel(), [searchUnmappedSources])

  const handleDelete = (sourceValueId) => {
    setIsSaving(true)
    deleteDimensionMappingRow(sourceDimension, sourceValueId)
      .then(() => refetchAll())
      .catch(notifyMappingError)
      .finally(() => {
        setIsSaving(false)
        setDeleteId(null)
      })
  }

  const handleDownload = () =>
    downloadDimensionMappingRows(sourceDimension, filters).catch(notifyMappingError)

  const clearSelection = () => {
    setSelectedSources([])
    setBulkValues({})
    setBulkErrors([])
    setSelectedLabels({})
    setRowDrafts({})
  }

  const onSelectionChange = (keys, rows) => {
    setSelectedSources(keys)
    setBulkErrors([])
    setSelectedLabels((prev) => {
      const merged = {
        ...prev,
        ...Object.fromEntries(
          rows
            .filter(Boolean)
            .map((row) => [row.source.id, `${row.source.code} - ${row.source.name}`]),
        ),
      }
      return Object.fromEntries(keys.filter((key) => merged[key]).map((key) => [key, merged[key]]))
    })
  }

  const handleDraftChange = (sourceValueId, target, value) => {
    const draft = { ...(rowDrafts[sourceValueId] ?? {}), [target]: value }
    setRowDrafts((prev) => ({ ...prev, [sourceValueId]: draft }))

    if (!mapping.targets.every((item) => draft[item])) return

    setMappingSource(sourceValueId)
    return bulkCreateDimensionMappingRows(sourceDimension, [
      { source_value_id: sourceValueId, values: draft },
    ])
      .then(() =>
        refetchAll(true).then(() =>
          setRowDrafts((prev) => {
            const next = { ...prev }
            delete next[sourceValueId]
            return next
          }),
        ),
      )
      .catch(notifyMappingError)
      .finally(() => setMappingSource(null))
  }

  const handleBulkAssign = () => {
    setIsSaving(true)
    setBulkErrors([])
    const values = isSubtype
      ? Object.fromEntries(Object.entries(bulkValues).filter(([, ids]) => ids?.length))
      : bulkValues
    return bulkCreateDimensionMappingRows(
      sourceDimension,
      selectedSources.map((sourceValueId) => ({
        source_value_id: sourceValueId,
        values,
      })),
    )
      .then((response) => {
        notification.success({
          message: t('CONFIG_DIMENSION_MAPPING_BULK_SUCCESS', {
            count: response.payload.data.created,
          }),
        })
        clearSelection()
        return refetchAll()
      })
      .catch((error) => {
        const errors = error?.response?.data?.errors
        if (Array.isArray(errors) && errors.length > 0) {
          setBulkErrors(errors)
          return
        }
        notifyMappingError(error)
      })
      .finally(() => setIsSaving(false))
  }

  const resetPage = () => {
    if (tablePagination.page !== 1) {
      setTablePagination({ current: 1, pageSize: tablePagination.page_size })
    }
  }

  const onSearch = (value) => {
    setSearch(value)
    resetPage()
    clearSelection()
  }

  const onUnmappedChange = (checked) => {
    setOnlyUnmapped(checked)
    resetPage()
    clearSelection()
  }

  const onTargetFilterChange = (target, value) => {
    setTargetFilters((prev) => {
      const next = { ...prev }
      if (value) {
        next[target] = value
      } else {
        delete next[target]
      }
      return next
    })
    resetPage()
    clearSelection()
  }

  const targetValueOptions = (target) =>
    (valuesByTarget[target] ?? []).map((value) => ({
      value: value.id,
      label: `${value.code_destination} - ${value.name_destination}`,
    }))

  const renderHeader = () => (
    <Navigator
      floated={false}
      size="big"
      items={[
        { name: t('LABEL_CONFIG'), url: '/configuracion' },
        { name: t('CONFIG_DIMENSION_MAPPINGS'), url: '/configuracion/mapeos' },
        { name: mapping?.name ?? '' },
      ]}
    />
  )

  // En subtipos no tener relación no es un pendiente: quiere decir que no restringe nada.
  const unmappedLabel = isSubtype
    ? 'CONFIG_DIMENSION_MAPPING_SUBTYPE_TOTAL_UNRESTRICTED'
    : 'CONFIG_DIMENSION_MAPPING_TOTAL_UNMAPPED'
  const unmappedFilterLabel = isSubtype
    ? 'CONFIG_DIMENSION_MAPPING_SUBTYPE_FILTER_UNRESTRICTED'
    : 'CONFIG_DIMENSION_MAPPING_FILTER_UNMAPPED'

  const renderTotals = () => (
    <Space size="small">
      <Tag color="green">
        {`${t(
          isSubtype
            ? 'CONFIG_DIMENSION_MAPPING_SUBTYPE_TOTAL_RESTRICTED'
            : 'CONFIG_DIMENSION_MAPPING_TOTAL_MAPPED',
        )}: ${sourceTotal - unmappedCount}`}
      </Tag>
      <Tooltip title={t(unmappedFilterLabel)}>
        <Tag
          color={isSubtype ? 'default' : 'orange'}
          style={{ cursor: 'pointer' }}
          onClick={() => onUnmappedChange(!onlyUnmapped)}
        >
          {`${t(unmappedLabel)}: ${unmappedCount}`}
        </Tag>
      </Tooltip>
    </Space>
  )

  const renderFilters = () => (
    <Space direction="horizontal" size="middle" wrap>
      <Input.Search
        allowClear
        placeholder={t(
          isSubtype
            ? 'CONFIG_DIMENSION_MAPPING_SUBTYPE_SEARCH_PLACEHOLDER'
            : 'CONFIG_DIMENSION_MAPPING_SEARCH_PLACEHOLDER',
        )}
        onSearch={onSearch}
        style={{ width: 280 }}
      />
      {(mapping?.targets ?? []).map((target) => (
        <Select
          key={target}
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: 220 }}
          placeholder={mapping.labels?.[target] ?? target}
          value={targetFilters[target]}
          onChange={(value) => onTargetFilterChange(target, value)}
          options={targetValueOptions(target)}
        />
      ))}
      <Space size="small">
        <Switch checked={onlyUnmapped} onChange={onUnmappedChange} size="small" />
        <Typography.Body level={3}>{t(unmappedFilterLabel)}</Typography.Body>
      </Space>
    </Space>
  )

  const renderActions = () => (
    <ButtonActionBar>
      <Button.Default
        title="ACTION_DOWNLOAD"
        icon={<DownloadOutlined />}
        onClick={handleDownload}
      />
      {canEdit && (
        <Button.Default
          title="CONFIG_DIMENSION_MAPPING_IMPORT_ACTION"
          icon={<UploadOutlined />}
          onClick={() => setShowImportModal(true)}
        />
      )}
      {/* Con un solo destino el select de la celda ya alcanza. */}
      {canEdit && (isSubtype || !hasSingleTarget) && (
        <Button.Primary
          title="CONFIG_DIMENSION_MAPPING_ROW_MAP_ACTION"
          onClick={() => {
            // En subtipos los orígenes los pide el modal, recién cuando se elige el tipo.
            if (!isSubtype) loadUnmappedSources()
            setEditingRow(null)
            setShowMapModal(true)
          }}
        />
      )}
    </ButtonActionBar>
  )

  const isBulkReady =
    selectedSources.length > 0 &&
    (isSubtype
      ? (mapping?.targets ?? []).some((target) => bulkValues[target]?.length)
      : (mapping?.targets ?? []).every((target) => bulkValues[target]))

  const renderBulkError = ({ source_value_id, errors }) => (
    <List.Item>
      <Typography.Body level={3}>
        {`${selectedLabels[source_value_id] ?? source_value_id}: ${Object.values(errors)
          .flat()
          .join(', ')}`}
      </Typography.Body>
    </List.Item>
  )

  const renderBulkErrors = () => (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <Alert type="error" message={t('CONFIG_DIMENSION_MAPPING_BULK_ERRORS')} />
      <List
        size="small"
        dataSource={bulkErrors}
        renderItem={renderBulkError}
        style={{ maxHeight: 160, overflowY: 'auto' }}
      />
    </Space>
  )

  const renderBulkBar = () => (
    <Space direction="horizontal" size="middle" wrap>
      <Typography.Body level={2}>
        {t('CONFIG_DIMENSION_MAPPING_BULK_SELECTED', { count: selectedSources.length })}
      </Typography.Body>
      {mapping.targets.map((target) => (
        <Select
          key={target}
          mode={isSubtype ? 'multiple' : undefined}
          showSearch
          optionFilterProp="label"
          style={{ minWidth: 220, maxWidth: 420 }}
          placeholder={mapping.labels?.[target] ?? target}
          value={bulkValues[target]}
          onChange={(value) => setBulkValues((prev) => ({ ...prev, [target]: value }))}
          options={targetValueOptions(target)}
        />
      ))}
      <Button.Primary
        title="CONFIG_DIMENSION_MAPPING_BULK_ASSIGN_ACTION"
        disabled={!isBulkReady}
        loading={isSaving}
        onClick={handleBulkAssign}
      />
      <Button.Default title="ACTION_CANCEL" disabled={isSaving} onClick={clearSelection} />
    </Space>
  )

  const tableRows = rowsLoaded
    ? {
        results: dimensionMappingRowsList.results ?? [],
        count: dimensionMappingRowsList.count ?? 0,
      }
    : { results: [], count: 0 }

  const hasItemCondition = tableRows.results.some((row) => 'item_condition' in (row.source ?? {}))

  const renderValueSet = (valueSet) => {
    if (!valueSet.length)
      return (
        <Tooltip title={t('CONFIG_DIMENSION_MAPPING_SUBTYPE_UNRESTRICTED_HELP')}>
          <Tag>{t('CONFIG_DIMENSION_MAPPING_SUBTYPE_UNRESTRICTED')}</Tag>
        </Tooltip>
      )
    return (
      <Space size={[0, 4]} wrap>
        {valueSet.map((value) => (
          <Tag key={value.id}>{`${value.code} - ${value.name}`}</Tag>
        ))}
      </Space>
    )
  }

  const columns = [
    ...(isSubtype
      ? [
          {
            dataIndex: 'source',
            key: 'document_type',
            title: t('FIELD_DOCUMENT_TYPE'),
            width: 180,
            render: (source) => source.document_type,
          },
        ]
      : []),
    {
      dataIndex: 'source',
      title: mapping?.labels?.[sourceDimension] ?? sourceDimension,
      render: (source) => `${source.code} - ${source.name}`,
    },
    ...(hasItemCondition
      ? [
          {
            dataIndex: 'source',
            key: 'item_condition',
            title: t('CONFIG_DIMENSION_MAPPING_ITEM_CONDITION'),
            width: 180,
            render: (source) =>
              source.item_condition ? (
                t(ITEM_CONDITION_LABELS[source.item_condition])
              ) : (
                <Tooltip title={t('CONFIG_DIMENSION_MAPPING_ITEM_CONDITION_EMPTY_HELP')}>
                  <Tag color="orange">{t('CONFIG_DIMENSION_MAPPING_ITEM_CONDITION_EMPTY')}</Tag>
                </Tooltip>
              ),
          },
        ]
      : []),
    ...(mapping?.targets ?? []).map((target) => ({
      dataIndex: 'values',
      key: target,
      title: mapping.labels?.[target] ?? target,
      render: (values, row) => {
        if (isSubtype) return renderValueSet(values[target])

        const value = values?.[target]
        if (value) return `${value.code} - ${value.name}`
        if (row.is_mapped)
          return (
            <Tooltip title={t('CONFIG_DIMENSION_MAPPING_ROW_INCOMPLETE_HELP')}>
              <Tag color="orange">{t('CONFIG_DIMENSION_MAPPING_ROW_INCOMPLETE')}</Tag>
            </Tooltip>
          )
        if (!canEdit) return '-'
        return (
          <Select
            showSearch
            optionFilterProp="label"
            bordered={false}
            style={{ width: 220 }}
            placeholder={t('ACTION_SELECT')}
            value={rowDrafts[row.source.id]?.[target] ?? null}
            loading={mappingSource === row.source.id}
            disabled={isSaving || mappingSource === row.source.id}
            onChange={(selected) => handleDraftChange(row.source.id, target, selected)}
            options={targetValueOptions(target)}
          />
        )
      },
    })),
    ...(canEdit
      ? [
          {
            dataIndex: 'source',
            key: 'actions',
            title: (
              <Space size="small">
                {t('FIELD_ACTIONS')}
                {/* En subtipos los conjuntos se editan; en el resto sigue siendo borrar y rehacer. */}
                {!isSubtype && (
                  <Tooltip title={t('CONFIG_DIMENSION_MAPPING_ROW_EDIT_HELP')}>
                    <InfoCircleOutlined />
                  </Tooltip>
                )}
              </Space>
            ),
            align: 'right',
            render: (source, row) =>
              row.is_mapped && (
                <Popconfirm
                  placement="bottomRight"
                  title={t('CONFIG_DIMENSION_MAPPING_ROW_DELETE_CONFIRMATION')}
                  okText={t('ACTION_DELETE')}
                  cancelText={t('ACTION_CANCEL')}
                  okButtonProps={{ loading: isSaving }}
                  cancelButtonProps={{ disabled: isSaving }}
                  onConfirm={() => handleDelete(source.id)}
                  onCancel={() => setDeleteId(null)}
                  visible={deleteId === source.id}
                >
                  <Space size="small">
                    {isSubtype && (
                      <Button.Icon
                        title="ACTION_EDIT"
                        icon={<EditOutlined />}
                        onClick={() => {
                          setEditingRow(row)
                          setShowMapModal(true)
                        }}
                      />
                    )}
                    <Button.Icon
                      title="ACTION_DELETE"
                      icon={<DeleteOutlined />}
                      onClick={() => setDeleteId(source.id)}
                    />
                  </Space>
                </Popconfirm>
              ),
          },
        ]
      : []),
  ]

  return (
    <SiderLayout>
      <Row gutter={[12, 12]}>
        <Col span={24}>{renderHeader()}</Col>
        {mapping && !mapping.is_editable && (
          <Col span={24}>
            <Tooltip title={t('CONFIG_DIMENSION_MAPPING_MANAGED_EXTERNALLY_TOOLTIP')}>
              <Tag color="blue">
                {t('CONFIG_DIMENSION_MAPPING_MANAGED_EXTERNALLY', {
                  system: mapping.source_system,
                })}
              </Tag>
            </Tooltip>
          </Col>
        )}
        {sourceTotal !== null && unmappedCount !== null && <Col span={24}>{renderTotals()}</Col>}
        <Col span={24}>{renderFilters()}</Col>
        {mapping && <Col span={24}>{renderActions()}</Col>}
        {canEdit && selectedSources.length > 0 && <Col span={24}>{renderBulkBar()}</Col>}
        {bulkErrors.length > 0 && <Col span={24}>{renderBulkErrors()}</Col>}
        <Col span={24}>
          <Spin spinning={isLoading}>
            <Table
              size="small"
              rowKey={(row) => row.source.id}
              columns={columns}
              dataSource={tableRows.results}
              loading={isSaving}
              onChange={setTablePagination}
              rowSelection={
                canEdit
                  ? {
                      selectedRowKeys: selectedSources,
                      onChange: onSelectionChange,
                      preserveSelectedRowKeys: true,
                      getCheckboxProps: (row) => ({ disabled: row.is_mapped }),
                    }
                  : undefined
              }
              pagination={{
                pageSize: tablePagination.page_size,
                current: tablePagination.page,
                total: tableRows.count,
                size: 'small',
                pageSizeOptions: ['10', '20', '30'],
                showSizeChanger: true,
              }}
            />
          </Spin>
        </Col>
      </Row>
      {mapping && (
        <>
          <MapRowModal
            visible={showMapModal}
            onClose={() => {
              setShowMapModal(false)
              setEditingRow(null)
            }}
            onRefetch={refetchAll}
            mapping={mapping}
            unmappedSources={unmappedSources}
            onSearchSources={searchUnmappedSources}
            onLoadSources={loadUnmappedSources}
            documentTypes={documentTypes}
            editingRow={editingRow}
            valuesByTarget={valuesByTarget}
          />
          <ImportRowsModal
            visible={showImportModal}
            onClose={() => setShowImportModal(false)}
            onRefetch={refetchAll}
            mapping={mapping}
          />
        </>
      )}
    </SiderLayout>
  )
}

const mapStateToProps = (state) => ({
  dimensionMappingsList: configuration.selectors.getDimensionMappingsList(state),
  dimensionMappingRowsList: configuration.selectors.getDimensionMappingRowsList(state),
})

const mapDispatchToProps = {
  fetchDimensionMappings: configuration.actions.fetchDimensionMappings,
  fetchDimensionMappingRows: configuration.actions.fetchDimensionMappingRows,
  fetchDimensionMappingRowsLookup: configuration.actions.fetchDimensionMappingRowsLookup,
  bulkCreateDimensionMappingRows: configuration.actions.bulkCreateDimensionMappingRows,
  deleteDimensionMappingRow: configuration.actions.deleteDimensionMappingRow,
  downloadDimensionMappingRows: configuration.actions.downloadDimensionMappingRows,
  fetchAllDimensions: configuration.actions.fetchAllDimensions,
  fetchAllDimensionsValues: configuration.actions.fetchAllDimensionsValues,
  fetchDocumentTypes: configuration.actions.fetchDocumentTypes,
}

export default connect(mapStateToProps, mapDispatchToProps)(DimensionMappingDetail)
